import axios from 'axios'

import { getAuthorizedConfiguration } from '../helpers/RequestHelper'

/**
 * Action types
 */
export const IS_LOADING_SUBSTITUTE_MATERIALS = 'IS_LOADING_SUBSTITUTE_MATERIALS'
export const SET_SUBSTITUTE_MATERIALS = 'SET_SUBSTITUTE_MATERIALS'
export const IS_SAVING_SUBSTITUTE_MATERIAL = 'IS_SAVING_SUBSTITUTE_MATERIAL'
export const IS_IMPORTING_SUBSTITUTE_MATERIALS = 'IS_IMPORTING_SUBSTITUTE_MATERIALS'

/**
 * Default page size for the catalog listing, matching the other administration views.
 */
export const DEFAULT_PAGE_SIZE = 10

/**
 * Is loading the substitute materials catalog
 * @param {*} status
 */
export const isLoadingSubstituteMaterials = (status) => {
	return {
		type: IS_LOADING_SUBSTITUTE_MATERIALS,
		status
	}
}

/**
 * Set the catalog page returned by the server
 * @param {*} data
 */
export const setSubstituteMaterials = (data) => {
	return {
		type: SET_SUBSTITUTE_MATERIALS,
		data
	}
}

/**
 * Read the substitute materials catalog.
 *
 * `GET /SubstituteMaterials` answers the entire catalog unfiltered — there is no paging, sorting or
 * filtering on the wire. Search, ordering and pagination are resolved in the client over the loaded
 * collection; see `getCatalogPage` in the selectors.
 *
 * That is not a shortcut: the catalog is a small, bounded reference table (~200 relations), and its
 * other consumer — the spare parts request flow — needs every row at once to correlate a SAP
 * listing. One read that serves both beats a paging contract that fits neither.
 *
 * This thunk deliberately does NOT short-circuit when the store already holds records. The catalog
 * is mutated by manual maintenance and by file import, and both need the listing to reflect their
 * result, which is only possible if asking again always reaches the server.
 */
export const loadSubstituteMaterials = () => {
	return function(dispatch, getState) {
		dispatch(isLoadingSubstituteMaterials(true))

		let user = getState().oidc.user
		let config = getAuthorizedConfiguration(user)
		let url = `${window.config.serviceManagementBackendUrl}/SubstituteMaterials`

		return axios
			.get(url, config)
			.then((serverResponse) => {
				dispatch(isLoadingSubstituteMaterials(false))
				// Every resolved response is a 2xx -- axios rejects the rest -- so the catalog is
				// taken from whichever success code the server answers with. Reading it only on a
				// 200 left the listing empty and silent the day that changed, with no error to
				// tell anyone why.
				if (serverResponse.data) {
					dispatch(setSubstituteMaterials(serverResponse.data))
				}
				return Promise.resolve()
			})
			.catch((error) => {
				dispatch(isLoadingSubstituteMaterials(false))
				console.error('Error loading the substitute materials catalog', error)
				return Promise.reject(error.response && error.response.data ? error.response.data.Message : null)
			})
	}
}

/**
 * Is saving a substitute material relation
 * @param {*} status
 */
export const isSavingSubstituteMaterial = (status) => {
	return {
		type: IS_SAVING_SUBSTITUTE_MATERIAL,
		status
	}
}

/**
 * Shape a failed request into something the container can reason about.
 *
 * The repo's usual pattern rejects with `error.response.data.Message`, a bare string. That is
 * enough to show a toaster but not to tell a business rejection apart from an unexpected failure,
 * which is what the duplicate-material requirement needs. Keeping the status alongside the message
 * is the smallest thing that makes that distinction possible.
 * @param {*} error
 */
export const toRequestError = (error) => {
	let response = error ? error.response : null
	return {
		status: response ? response.status : null,
		message: response && response.data ? response.data.Message : null
	}
}

/**
 * Whether a failed save was rejected because the discontinued material is already registered.
 *
 * A discontinued material has exactly one substitute, so the server enforces uniqueness on its
 * code. HTTP 409 is the status that states a uniqueness conflict; the message heuristic is a
 * fallback for the case where the backend answers a generic 400 instead.
 *
 * This is the one assumption in this capability that depends on the backend's error contract. When
 * it does not hold, the caller still shows the server's message — it just cannot point at the field.
 * @param {*} requestError produced by toRequestError
 */
export const isDuplicateMaterialError = (requestError) => {
	if (!requestError) return false
	if (requestError.status === 409) return true
	if (!requestError.message) return false
	let message = String(requestError.message).toLowerCase()
	return message.includes('already') || message.includes('duplicate') || message.includes('registrado')
}

/**
 * Register a new substitute material relation.
 *
 * Codes travel as the user captured them, trimmed of surrounding whitespace only. Normalizing
 * further — dropping leading zeros — belongs to the server, which is where uniqueness lives;
 * doing it here would store a code different from the one the user typed.
 * @param {*} model
 */
export const addSubstituteMaterial = (model) => {
	return function(dispatch, getState) {
		dispatch(isSavingSubstituteMaterial(true))

		let user = getState().oidc.user
		let config = getAuthorizedConfiguration(user)
		let url = `${window.config.serviceManagementBackendUrl}/SubstituteMaterials/add`

		return axios
			.post(url, toCommand(model), config)
			.then((serverResponse) => {
				dispatch(isSavingSubstituteMaterial(false))
				return Promise.resolve(serverResponse.data)
			})
			.catch((error) => {
				dispatch(isSavingSubstituteMaterial(false))
				console.error('Error adding the substitute material', error)
				return Promise.reject(toRequestError(error))
			})
	}
}

/**
 * Update an existing substitute material relation.
 * @param {*} model
 */
export const updateSubstituteMaterial = (model) => {
	return function(dispatch, getState) {
		dispatch(isSavingSubstituteMaterial(true))

		let user = getState().oidc.user
		let config = getAuthorizedConfiguration(user)
		let url = `${window.config.serviceManagementBackendUrl}/SubstituteMaterials`
		let command = { ...toCommand(model), Token: model && model.Token ? model.Token : '' }

		return axios
			.put(url, command, config)
			.then((serverResponse) => {
				dispatch(isSavingSubstituteMaterial(false))
				return Promise.resolve(serverResponse.data)
			})
			.catch((error) => {
				dispatch(isSavingSubstituteMaterial(false))
				console.error('Error updating the substitute material', error)
				return Promise.reject(toRequestError(error))
			})
	}
}

/**
 * Remove a substitute material relation.
 *
 * The removal is definitive: the catalog has no active/inactive state, so records are kept or
 * deleted outright.
 * @param {*} token
 */
export const removeSubstituteMaterial = (token) => {
	return function(dispatch, getState) {
		dispatch(isSavingSubstituteMaterial(true))

		let user = getState().oidc.user
		let config = getAuthorizedConfiguration(user)
		let url = `${window.config.serviceManagementBackendUrl}/SubstituteMaterials/${token}`

		return axios
			.delete(url, config)
			.then(() => {
				dispatch(isSavingSubstituteMaterial(false))
				return Promise.resolve()
			})
			.catch((error) => {
				dispatch(isSavingSubstituteMaterial(false))
				console.error('Error removing the substitute material', error)
				return Promise.reject(toRequestError(error))
			})
	}
}

/**
 * Is importing the catalog from a file
 * @param {*} status
 */
export const isImportingSubstituteMaterials = (status) => {
	return {
		type: IS_IMPORTING_SUBSTITUTE_MATERIALS,
		status
	}
}

/**
 * Whether the request failed because the server has no such operation.
 *
 * A 404 means the route does not exist; a 405 means it exists under another verb. Either way the
 * operation is not available, which is a different thing from the operation having been attempted
 * and failed — and the difference matters to whoever is looking at the screen. Reporting "the
 * import could not be completed" for a route that was never there sends the reader hunting for a
 * problem in their file.
 *
 * Only the catalog read is served today; adding, editing, removing and importing are the server
 * side of the administration story and are not built yet.
 * @param {*} requestError produced by toRequestError
 */
export const isEndpointUnavailableError = (requestError) => {
	if (!requestError) return false
	return requestError.status === 404 || requestError.status === 405 || requestError.status === 501
}

/**
 * Whether the request was rejected before it reached the operation at all.
 *
 * The service authenticates ahead of routing: every route answers 401 without a valid token,
 * existing or not. So a 401 says nothing about the operation — it says the session is no longer
 * good — and it must be read BEFORE the unavailable check, which would otherwise be asked to
 * interpret a status that never got as far as the route.
 *
 * This is the status an expired session produces, and it carries no message body. Left
 * unclassified it surfaced as "the import could not be completed", which points the reader at
 * their file when the actual fix is to sign in again.
 * @param {*} requestError produced by toRequestError
 */
export const isUnauthorizedError = (requestError) => {
	if (!requestError) return false
	return requestError.status === 401 || requestError.status === 403
}

/**
 * What to say for each of the two rejections above. 401 is a session that is no longer valid, which
 * the reader fixes by signing in; 403 is a session that is valid but not entitled, which they
 * cannot fix themselves. Telling them apart is the difference between a retry and a request to an
 * administrator.
 * @param {*} requestError produced by toRequestError
 */
export const buildUnauthorizedMessage = (requestError) => {
	if (requestError && requestError.status === 403) {
		return 'Your account is not allowed to modify the substitute materials catalog.'
	}
	return 'Your session is no longer valid, so nothing was changed. Sign in again and retry.'
}

/**
 * Shown when the request never got an answer that could be read.
 *
 * `toRequestError` leaves the status null when the failure carried no response at all — the service
 * is not running, the TLS certificate was rejected, the connection dropped. That is a different
 * problem from anything the server could have replied, and it is the one thing the reader can act
 * on directly.
 */
export const NO_RESPONSE_MESSAGE = 'The server did not respond, so nothing was changed. Check that the service is running, then try again.'

/**
 * Describe a failure none of the classifiers could name.
 *
 * The server's own message wins when there is one. Otherwise the status code goes into the text:
 * an operation whose server side is still being built fails in ways the portal cannot interpret,
 * and a message that names the status lets whoever is looking at the screen say what happened
 * without opening the browser console. A generic sentence with the status hidden is what turns a
 * missing endpoint into a support conversation.
 *
 * @param {*} requestError produced by toRequestError
 * @param {*} fallbackMessage what to say when the status is all there is
 */
export const describeUnclassifiedError = (requestError, fallbackMessage) => {
	if (!requestError) return NO_RESPONSE_MESSAGE
	if (requestError.message) return requestError.message
	// Covers null, undefined and 0 in one test. Axios reports status 0 on a request that was
	// aborted or blocked before a response existed, and "(HTTP 0)" reads as the same noise this
	// function was written to replace.
	if (!requestError.status) return NO_RESPONSE_MESSAGE
	return `${fallbackMessage} (HTTP ${requestError.status})`
}

/**
 * Whether a failed import was rejected because the file's structure is not the expected one.
 *
 * Told apart from an unexpected failure so the user learns the cause is their file and can pick
 * another, instead of reading a generic error. HTTP 415 states an unsupported payload; the message
 * heuristic covers a backend that answers a generic 400 instead.
 *
 * Same backend-contract assumption as the duplicate-material rejection; when it does not hold the
 * server's message is still shown.
 * @param {*} requestError produced by toRequestError
 */
export const isInvalidFileFormatError = (requestError) => {
	if (!requestError) return false
	if (requestError.status === 415) return true
	if (!requestError.message) return false
	let message = String(requestError.message).toLowerCase()
	return message.includes('format') || message.includes('structure') || message.includes('formato') || message.includes('estructura')
}

/**
 * Import the catalog from a file.
 *
 * The server processes the file in a single transaction, so this either concludes with an aggregate
 * result or is rejected — there is no partial outcome to represent here.
 * @param {*} file
 */
export const importSubstituteMaterials = (file) => {
	return function(dispatch, getState) {
		dispatch(isImportingSubstituteMaterials(true))

		let user = getState().oidc.user
		let config = getAuthorizedConfiguration(user)
		let url = `${window.config.serviceManagementBackendUrl}/SubstituteMaterials/import`
		let data = new FormData()
		data.append('file', file)
		if (file && file.name) {
			data.append('FileName', file.name)
		}

		return axios
			.post(url, data, config)
			.then((serverResponse) => {
				dispatch(isImportingSubstituteMaterials(false))
				return Promise.resolve(serverResponse.data)
			})
			.catch((error) => {
				dispatch(isImportingSubstituteMaterials(false))
				console.error('Error importing the substitute materials catalog', error)
				return Promise.reject(toRequestError(error))
			})
	}
}

/**
 * Build the aggregate message describing what the import did.
 *
 * Reports counts, never the per-row detail: the file can carry hundreds of relations, and what the
 * user needs to know is how many went in, how many changed and how many were left out. A count
 * that the server did not report is omitted rather than shown as zero, so the message never claims
 * something the response did not say.
 * @param {*} result payload returned by the import
 */
export const buildImportResultMessage = (result) => {
	if (!result) {
		return 'The import finished, but its result could not be read.'
	}

	let parts = []
	let read = (value) => (typeof value === 'number' ? value : null)
	let inserted = read(result.Inserted)
	let updated = read(result.Updated)
	let skipped = read(result.Skipped)

	if (inserted !== null) parts.push(`${inserted} added`)
	if (updated !== null) parts.push(`${updated} updated`)
	if (skipped !== null) parts.push(`${skipped} skipped`)

	if (parts.length === 0) {
		return 'The import finished, but its result could not be read.'
	}
	return `Import finished: ${parts.join(', ')}.`
}

/**
 * Build the command sent for an add or an update, trimming surrounding whitespace.
 * @param {*} model
 */
export const toCommand = (model) => {
	let trim = (value) => (value ? String(value).trim() : '')
	return {
		ObsoleteMaterialCode: trim(model ? model.ObsoleteMaterialCode : ''),
		ObsoleteMaterialDescription: trim(model ? model.ObsoleteMaterialDescription : ''),
		SubstituteMaterialCode: trim(model ? model.SubstituteMaterialCode : ''),
		SubstituteMaterialDescription: trim(model ? model.SubstituteMaterialDescription : '')
	}
}

/**
 * Extract the catalog rows from the server payload.
 *
 * Tolerates a payload whose row array is absent or named differently, degrading to an empty
 * listing rather than throwing: the endpoint's exact shape is still being settled with backend,
 * and a naming mismatch should leave the view usable instead of breaking it.
 * @param {*} data
 */
export const getItemsFromResponse = (data) => {
	if (!data) {
		return []
	}
	if (Array.isArray(data)) {
		return data
	}
	if (Array.isArray(data.substituteMaterials)) {
		return data.substituteMaterials
	}
	if (Array.isArray(data.items)) {
		return data.items
	}
	return []
}

/**
 * Load the catalog for a consumer that correlates against it rather than browsing it.
 *
 * Same read as `loadSubstituteMaterials` — one endpoint answers the whole catalog — differing only
 * in its failure policy: this one never rejects. The spare parts request flow dispatches it
 * alongside its SAP serie lookup, and the catalog there only drives an advisory warning, so a
 * failure must degrade to "no warnings" rather than take the serie consult down with it or surface
 * an error the user can do nothing about. The administration listing, by contrast, does want to
 * report that its own content failed to load.
 *
 * With the request left unfulfilled the store keeps whatever it had — an empty listing on first
 * load — so the derived index comes out empty and no row is flagged.
 */
export const loadFullSubstituteMaterialsCatalog = () => {
	return function(dispatch) {
		return dispatch(loadSubstituteMaterials()).catch(() => {
			// Deliberately swallowed: see above.
			return Promise.resolve()
		})
	}
}
